import { Slider } from '@base-ui/react/slider';
import { useId, type ReactNode } from 'react';

import { NumberInput } from '@/ui/inputs.tsx';

export type CalculatorInputProps = {
  label: string;
  value: number;
  setValue: (value: number) => void;
  min: number;
  max: number;
  step: number;
  className?: string;
  formatValue: (value: number) => string;
  endAdornment?: ReactNode;
  /** Log scale gives finer control at the low end of a wide range (e.g. loan amounts). */
  scale?: 'linear' | 'log';
};

const snapToStep = (value: number, min: number, max: number, step: number) => {
  const snapped = Math.round((value - min) / step) * step + min;
  return Math.min(Math.max(snapped, min), max);
};

export const CalculatorInput = ({
  label,
  value,
  setValue,
  min,
  max,
  step,
  className,
  formatValue,
  endAdornment,
  scale = 'linear',
}: CalculatorInputProps) => {
  const inputId = useId();

  const isLog = scale === 'log';
  const sliderMin = isLog ? Math.log10(min) : min;
  const sliderMax = isLog ? Math.log10(max) : max;
  const sliderValue = isLog ? Math.log10(value) : value;
  const sliderStep = isLog ? (sliderMax - sliderMin) / 100 : step;

  const commitValue = (next: number) => {
    setValue(snapToStep(next, min, max, step));
  };

  const fromSlider = (sliderNext: number) =>
    isLog ? Math.round(Math.pow(10, sliderNext)) : sliderNext;

  return (
    <div className={className}>
      <label htmlFor={inputId} className="mb-2 block typography-v1-body4">
        {label}
      </label>

      <NumberInput
        id={inputId}
        value={value}
        min={min}
        max={max}
        step={step}
        format={{ maximumFractionDigits: 0 }}
        endAdornment={endAdornment}
        onValueCommitted={(next) => commitValue(next ?? min)}
        className="mb-2"
      />

      <Slider.Root
        value={sliderValue}
        onValueChange={(next) => {
          const sliderNext = Array.isArray(next) ? next[0] : next;
          commitValue(fromSlider(sliderNext));
        }}
        min={sliderMin}
        max={sliderMax}
        step={sliderStep}
        thumbAlignment="edge"
      >
        <Slider.Control className="flex touch-none items-center py-3 select-none">
          <Slider.Track className="h-1.5 w-full rounded bg-v1-neutral-40 select-none">
            <Slider.Indicator className="rounded bg-v1-primary-100 select-none" />
            <Slider.Thumb
              aria-label={label}
              className="size-7 rounded-full bg-v1-primary-100 outline-2 outline-white select-none has-focus-visible:ring-3 has-focus-visible:ring-v1-primary-100 has-focus-visible:outline-2"
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 22 22"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="m-0.75"
                aria-hidden
              >
                <path
                  d="M2.66832 10.6665L6.66832 6.6665L7.9572 7.95539L5.24609 10.6665L7.9572 13.3776L6.66832 14.6665L2.66832 10.6665ZM13.2905 7.95539L14.5794 6.6665L18.5794 10.6665L14.5794 14.6665L13.2905 13.3776L16.0016 10.6665L13.2905 7.95539Z"
                  fill="white"
                />
              </svg>
            </Slider.Thumb>
          </Slider.Track>
        </Slider.Control>
      </Slider.Root>

      <div className="mt-1 flex justify-between">
        <span className="typography-v1-caption1 text-v1-neutral-80">{formatValue(min)}</span>
        <span className="typography-v1-caption1 text-v1-neutral-80">{formatValue(max)}</span>
      </div>
    </div>
  );
};
